import React, { Suspense, lazy, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Loader2, Play, RotateCcw, X } from "lucide-react";
import { publicShowApi, GOLD } from "@/components/nds/windows/showcase/showKit";

const ShowcasePlayer = lazy(() => import("@/components/nds/windows/showcase/ShowcasePlayer"));
export const segSlug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const goFull = () => document.documentElement.requestFullscreen?.().catch(() => {});

function Screen({ s, children, testid }) {
  return (
    <div data-testid={testid} className="fixed inset-0 bg-[#05070d] text-white flex flex-col items-center justify-center text-center px-6">
      <div className="font-mono text-xs sm:text-sm uppercase tracking-[0.4em]" style={{ color: GOLD }}>{s.act === 1 ? "Act One · Platform Capabilities" : "Act Two · Resident Impact Story"}</div>
      <div className="font-serif text-[28vw] sm:text-[12vw] leading-none font-black text-white/10 mt-4">{String(s.n).padStart(2, "0")}</div>
      <h1 data-testid="watch-seg-title" className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold -mt-[8vw] sm:-mt-[6vw]">{s.title}</h1>
      <p className="text-base sm:text-lg text-white/60 mt-4">{s.kicker}</p>
      <div className="mt-10 flex flex-wrap justify-center gap-3">{children}</div>
      <Link to="/" data-testid="watch-home" className="absolute top-5 left-6 font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 hover:text-white/80 transition-colors">The Exchange Alliance</Link>
    </div>
  );
}

const btn = "inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full font-semibold transition-transform hover:scale-[1.03]";

export default function WatchSegmentPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [show, setShow] = useState(null);
  const [err, setErr] = useState(false);
  const [st, setSt] = useState({ slug, m: "start" });
  const mode = st.slug === slug ? st.m : "start";
  const setMode = (m) => setSt({ slug, m });
  useEffect(() => { publicShowApi.get().then(setShow).catch(() => setErr(true)); }, []);
  if (err) return <div data-testid="watch-error" className="fixed inset-0 grid place-items-center bg-[#05070d] text-white/70">The showcase couldn't load. Please refresh.</div>;
  if (!show) return <div data-testid="watch-loading" className="fixed inset-0 grid place-items-center bg-[#05070d]" style={{ color: GOLD }}><Loader2 className="h-8 w-8 animate-spin" /></div>;
  const i = show.segments.findIndex((s) => segSlug(s.title) === slug);
  if (i < 0) {
    return (
      <div data-testid="watch-not-found" className="fixed inset-0 flex flex-col items-center justify-center gap-4 bg-[#05070d] text-white/70">
        <p>That segment doesn't exist.</p>
        <Link to={`/watch/${segSlug(show.segments[0].title)}`} className="underline" style={{ color: GOLD }}>Start from Segment 1</Link>
      </div>);
  }
  const s = show.segments[i];
  const nextSeg = show.segments[i + 1];
  const play = () => { goFull(); setMode("play"); };
  if (mode === "play") return <Suspense fallback={null}><ShowcasePlayer show={show} segIdx={[i]} onExit={() => setMode("end")} api={publicShowApi} /></Suspense>;
  if (mode === "end") {
    return (
      <Screen s={s} testid="watch-end">
        <button type="button" onClick={play} data-testid="watch-replay" className={`${btn} border border-white/25 text-white`}><RotateCcw className="h-4 w-4" /> Replay</button>
        {nextSeg && <button type="button" onClick={() => { goFull(); setSt({ slug: segSlug(nextSeg.title), m: "play" }); nav(`/watch/${segSlug(nextSeg.title)}`); }} data-testid="watch-next" className={`${btn} text-black`} style={{ background: GOLD }}>
          Next · {nextSeg.title} <ArrowRight className="h-4 w-4" /></button>}
        <Link to="/" data-testid="watch-close" className={`${btn} border border-white/15 text-white/70`}><X className="h-4 w-4" /> Close</Link>
      </Screen>);
  }
  return (
    <Screen s={s} testid="watch-start">
      <button type="button" onClick={play} data-testid="watch-play" className={`${btn} text-black text-lg px-9 py-4`} style={{ background: GOLD }}><Play className="h-5 w-5 fill-current" /> Play</button>
    </Screen>
  );
}
